/**
 * Octopus IoT Platform - Geofence Upload Modal
 * FASE 2: Modal for uploading KMZ/KML files to create geofences
 */
import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Upload, FileUp, MapPin, Loader2, X, Check } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { geofenceApi } from '@/services/geofenceApi';
import GeofenceMap from './GeofenceMap';

// Preset colors for geofences
const COLOR_PRESETS = [
  { value: '#FF6B35', label: 'Laranja' },
  { value: '#22C55E', label: 'Verde' },
  { value: '#3B82F6', label: 'Azul' },
  { value: '#F59E0B', label: 'Amarelo' },
  { value: '#EF4444', label: 'Vermelho' },
  { value: '#8B5CF6', label: 'Roxo' },
];

const normalizeHexColor = (value, fallback = '#FF6B35') => (
  /^#[0-9A-Fa-f]{6}$/.test(value || '') ? value.toUpperCase() : fallback
);

/**
 * GeofenceUploadModal Component
 * 
 * @param {boolean} open - Whether modal is open
 * @param {function} onOpenChange - Callback when open state changes
 * @param {function} onSuccess - Callback when upload succeeds
 */
const GeofenceUploadModal = ({ open, onOpenChange, onSuccess, targetGroup = null }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState('#FF6B35');
  const [borderColor, setBorderColor] = useState('#FF6B35');
  const [borderWidth, setBorderWidth] = useState(2);
  const [fillMode, setFillMode] = useState('transparent');
  const [transparencyPercent, setTransparencyPercent] = useState(70);
  const [colorMode, setColorMode] = useState('preserve');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [importMode, setImportMode] = useState('together');
  const [uploading, setUploading] = useState(false);
  const [uploadedGeofence, setUploadedGeofence] = useState(null);
  const [uploadedCount, setUploadedCount] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const [fileHasColors, setFileHasColors] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const fileInputRef = useRef(null);

  // Reset form
  const resetForm = () => {
    setName('');
    setDescription('');
    setColor('#FF6B35');
    setBorderColor('#FF6B35');
    setBorderWidth(2);
    setFillMode('transparent');
    setTransparencyPercent(70);
    setColorMode('preserve');
    setSelectedFiles([]);
    setImportMode('together');
    setUploadedGeofence(null);
    setUploadedCount(0);
    setDragOver(false);
    setFileHasColors(false);
    setPreviewing(false);
  };
  
  // Handle file selection
  const handleFileSelect = async (files) => {
    const nextFiles = Array.from(files || []).filter(Boolean);
    if (!nextFiles.length) return;
    
    const validExtensions = ['.kmz', '.kml'];
    const invalidFile = nextFiles.find((file) => {
      const ext = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
      return !validExtensions.includes(ext);
    });
    
    if (invalidFile) {
      toast.error('Arquivo inválido', {
        description: 'Por favor, selecione um arquivo .kmz ou .kml'
      });
      return;
    }
    
    if (nextFiles.some((file) => file.size > 10 * 1024 * 1024)) {
      toast.error('Arquivo muito grande', {
        description: 'Cada arquivo deve ter no máximo 10MB'
      });
      return;
    }
    
    setSelectedFiles(nextFiles);
    setFileHasColors(false);

    // Auto-fill name from filename if empty
    if (!name) {
      const baseName = nextFiles.length === 1
        ? nextFiles[0].name.replace(/\.(kmz|kml)$/i, '')
        : targetGroup?.name || 'Grupo de geofences';
      setName(baseName);
    }

    // Preview file to check for embedded colors
    setPreviewing(true);
    try {
      const previews = await Promise.allSettled(nextFiles.map((file) => geofenceApi.previewFile(file)));
      setFileHasColors(previews.some((result) => result.status === 'fulfilled' && result.value?.data?.has_colors));
    } catch {
      // Preview is best-effort; if it fails we just keep the color picker enabled
    } finally {
      setPreviewing(false);
    }
  };
  
  // Handle drag events
  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };
  
  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };
  
  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFileSelect(e.dataTransfer.files);
  };
  
  // Handle upload
  const handleUpload = async () => {
    if (!selectedFiles.length || !name.trim()) {
      toast.error('Dados incompletos', {
        description: 'Selecione ao menos um arquivo e preencha o nome'
      });
      return;
    }
    
    setUploading(true);
    
    try {
      let groupId = targetGroup?.id || null;
      if (!groupId && selectedFiles.length > 1 && importMode === 'together') {
        const groupResponse = await geofenceApi.createGeofenceGroup({
          name: name.trim(),
          description: description.trim() || null,
        });
        groupId = groupResponse.data?.id;
      }

      const responses = [];
      for (const file of selectedFiles) {
        const formData = new FormData();
        const fileBaseName = file.name.replace(/\.(kmz|kml)$/i, '');
        formData.append('file', file);
        formData.append('name', selectedFiles.length === 1 || groupId ? name.trim() : fileBaseName);
        formData.append('description', description.trim() || '');
        formData.append('color', color);
        formData.append('border_color', borderColor);
        formData.append('border_width', String(borderWidth || 2));
        formData.append('fill_enabled', fillMode === 'outline' ? 'false' : 'true');
        formData.append('fill_opacity', fillMode === 'solid'
          ? '1'
          : fillMode === 'transparent'
            ? String(Math.max(0, Math.min(100, 100 - Number(transparencyPercent || 0))) / 100)
            : '0'
        );
        formData.append('color_override', colorMode === 'overwrite' ? 'true' : 'false');
        if (groupId) {
          formData.append('source_group_id', groupId);
        }
        responses.push(await geofenceApi.uploadGeofence(formData));
      }

      const payloads = responses.map((response) => response.data || {});
      const primary = payloads[0]?.primary_geofence || payloads[0];
      const count = payloads.reduce((sum, payload) => sum + Number(payload.created_count || 1), 0);

      setUploadedGeofence(primary);
      setUploadedCount(count);
      toast.success('Geofence criada', {
        description: count > 1
          ? `${count} geometrias importadas do arquivo com sucesso`
          : `"${primary.name}" foi criada com sucesso`
      });
      
    } catch (error) {
      console.error('Upload error:', error);
      const errorMsg = error.response?.data?.detail || 'Erro ao processar arquivo';
      toast.error('Erro no upload', {
        description: errorMsg
      });
    } finally {
      setUploading(false);
    }
  };
  
  // Handle close and success
  const handleClose = () => {
    if (uploadedGeofence && onSuccess) {
      onSuccess(uploadedGeofence);
    }
    resetForm();
    onOpenChange(false);
  };
  
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); else onOpenChange(v); }}>
      <DialogContent className="sm:max-w-[600px]" data-testid="geofence-upload-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5 text-primary" />
            {targetGroup ? `Adicionar ao grupo ${targetGroup.name}` : 'Nova Geofence'}
          </DialogTitle>
          <DialogDescription>
            Faça upload de um ou mais arquivos KMZ/KML para criar polígonos ou waypoints
          </DialogDescription>
        </DialogHeader>
        
        {/* If already uploaded, show preview */}
        {uploadedGeofence ? (
          <div className="space-y-4">
            <div className="flex items-center gap-2 p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
              <Check className="h-5 w-5 text-green-500" />
              <span className="text-green-500 font-medium">
                {uploadedCount > 1
                  ? `${uploadedCount} geometrias importadas com sucesso!`
                  : 'Geofence criada com sucesso!'}
              </span>
            </div>
            
            <div className="space-y-2">
              <Label className="text-muted-foreground">Preview da geometria:</Label>
              <GeofenceMap 
                geofences={[uploadedGeofence]} 
                height="250px"
                interactive={false}
              />
            </div>
            
            <DialogFooter>
              <Button onClick={handleClose} data-testid="geofence-close-btn">
                Fechar
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-4">
            {/* File Upload Area */}
            <div className="space-y-2">
              <Label>Arquivo KMZ/KML</Label>
              <div
                className={cn(
                  "border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors",
                  dragOver ? "border-primary bg-primary/5" : "border-border hover:border-primary/50",
                  selectedFiles.length && "border-green-500 bg-green-500/5"
                )}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                data-testid="geofence-dropzone"
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".kmz,.kml"
                  multiple
                  className="hidden"
                  onChange={(e) => handleFileSelect(e.target.files)}
                />
                
                {selectedFiles.length ? (
                  <div className="flex items-center justify-center gap-2">
                    <FileUp className="h-8 w-8 text-green-500" />
                    <div className="text-left">
                      <p className="font-medium text-green-500">
                        {selectedFiles.length === 1 ? selectedFiles[0].name : `${selectedFiles.length} arquivos selecionados`}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {(selectedFiles.reduce((sum, file) => sum + file.size, 0) / 1024).toFixed(1)} KB
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="ml-2"
                      onClick={(e) => { e.stopPropagation(); setSelectedFiles([]); }}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <>
                    <Upload className="h-10 w-10 mx-auto mb-2 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">
                      Arraste arquivos KMZ ou KML aqui
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      ou clique para selecionar
                    </p>
                  </>
                )}
              </div>
            </div>

            {targetGroup && (
              <div className="rounded-lg border bg-muted/20 p-3 text-sm">
                Os arquivos serão adicionados ao grupo <span className="font-medium">{targetGroup.name}</span>.
              </div>
            )}

            {!targetGroup && selectedFiles.length > 1 && (
              <div className="space-y-2">
                <Label>Organização dos arquivos</Label>
                <Select value={importMode} onValueChange={setImportMode}>
                  <SelectTrigger data-testid="geofence-import-mode">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="together">Colocar todos em um grupo</SelectItem>
                    <SelectItem value="separate">Criar grupos separados por arquivo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {/* Name */}
            <div className="space-y-2">
              <Label htmlFor="geofence-name">Nome *</Label>
              <Input
                id="geofence-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ex: Porto de Santos - Zona de Fundeio"
                data-testid="geofence-name-input"
              />
            </div>
            
            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="geofence-desc">Descrição</Label>
              <Textarea
                id="geofence-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Descrição opcional da área"
                rows={2}
                data-testid="geofence-description-input"
              />
            </div>
            
            {/* Style */}
            <div className="space-y-2">
              <Label>
                Estilo
                {previewing && (
                  <Loader2 className="inline h-3 w-3 ml-1.5 animate-spin" />
                )}
              </Label>
              {fileHasColors && (
                <Select value={colorMode} onValueChange={setColorMode}>
                  <SelectTrigger data-testid="geofence-color-mode">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="preserve">Manter cores originais dos arquivos</SelectItem>
                    <SelectItem value="overwrite">Substituir por uma cor única</SelectItem>
                  </SelectContent>
                </Select>
              )}
              {(!fileHasColors || colorMode === 'overwrite') ? (
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Cor do preenchimento</Label>
                  <div className="flex flex-wrap gap-2">
                    {COLOR_PRESETS.map((preset) => (
                      <button
                        key={preset.value}
                        type="button"
                        className={cn(
                          "w-8 h-8 rounded-full border-2 transition-all",
                          color === preset.value ? "border-white scale-110" : "border-transparent"
                        )}
                        style={{ backgroundColor: preset.value }}
                        onClick={() => setColor(preset.value)}
                        title={preset.label}
                        data-testid={`geofence-color-${preset.value}`}
                      />
                    ))}
                  </div>
                  <div className="flex items-center gap-2">
                    <Input
                      type="color"
                      value={normalizeHexColor(color)}
                      onChange={(e) => setColor(e.target.value.toUpperCase())}
                      className="h-9 w-14 p-1"
                      aria-label="Selecionar cor personalizada"
                      data-testid="geofence-color-picker"
                    />
                    <Input
                      value={color}
                      onChange={(e) => setColor(e.target.value.toUpperCase())}
                      onBlur={() => setColor((current) => normalizeHexColor(current))}
                      className="h-9 w-28 font-mono"
                      aria-label="Cor hexadecimal"
                      data-testid="geofence-color-hex"
                    />
                  </div>
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">
                  As cores definidas no KMZ/KML serão preservadas para cada geometria.
                </p>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Cor da borda</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="color"
                      value={normalizeHexColor(borderColor)}
                      onChange={(e) => setBorderColor(e.target.value.toUpperCase())}
                      className="h-9 w-14 p-1"
                      aria-label="Selecionar cor da borda"
                      data-testid="geofence-border-color-picker"
                    />
                    <Input
                      value={borderColor}
                      onChange={(e) => setBorderColor(e.target.value.toUpperCase())}
                      onBlur={() => setBorderColor((current) => normalizeHexColor(current))}
                      className="h-9 w-28 font-mono"
                      aria-label="Cor hexadecimal da borda"
                      data-testid="geofence-border-color-hex"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Espessura da borda</Label>
                  <Input
                    type="number"
                    min="1"
                    max="12"
                    value={borderWidth}
                    onChange={(e) => setBorderWidth(Number(e.target.value))}
                    className="h-9"
                    data-testid="geofence-border-width"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Preenchimento</Label>
                  <Select value={fillMode} onValueChange={setFillMode}>
                    <SelectTrigger data-testid="geofence-fill-mode">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="solid">Sólido</SelectItem>
                      <SelectItem value="transparent">Transparente</SelectItem>
                      <SelectItem value="outline">Só borda</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {fillMode === 'transparent' && (
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Transparência (%)</Label>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      value={transparencyPercent}
                      onChange={(e) => setTransparencyPercent(Number(e.target.value))}
                      className="h-9"
                      data-testid="geofence-transparency-percent"
                    />
                  </div>
                )}
              </div>
            </div>
            
            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => { resetForm(); onOpenChange(false); }}
                data-testid="geofence-cancel-btn"
              >
                Cancelar
              </Button>
              <Button 
                onClick={handleUpload}
                disabled={uploading || !selectedFiles.length || !name.trim()}
                data-testid="geofence-upload-btn"
              >
                {uploading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Processando...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    Criar Geofence
                  </>
                )}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default GeofenceUploadModal;
